import { useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, X } from "lucide-react";
import { useLang, api } from "../store";
import { Reveal } from "../components/Reveal";
import Footer from "../components/Footer";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error(t("contact.tooBig"));
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const data = new FormData();
    data.append("name", form.name);
    data.append("email", form.email);
    data.append("message", form.message);
    if (file) data.append("attachment", file);
    try {
      await api.post("/contact-messages", data);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
      clearFile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const input = "w-full rounded border border-white/10 bg-[#0a0b0e] px-4 py-3 text-sm outline-none transition-colors focus:border-amber-500";
  const label = "font-mono2 mb-1.5 block text-[10px] uppercase tracking-[0.25em] text-slate-500";

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <div className="mx-auto max-w-[1600px] px-6 pb-24 pt-32 sm:pt-36">
        <div className="max-w-2xl">
          <Reveal>
            <form onSubmit={submit} className="dc-card space-y-5 rounded p-7" data-testid="contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={label}>{t("contact.name")}</label>
                  <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={input} data-testid="contact-name" />
                </div>
                <div>
                  <label className={label}>{t("contact.email")}</label>
                  <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={input} data-testid="contact-email" />
                </div>
              </div>

              <div>
                <label className={label}>{t("contact.message")}</label>
                <textarea required rows={7} value={form.message} onChange={(e) => set("message", e.target.value)} className={input} data-testid="contact-message" />
              </div>

              <div>
                <label className={label}>{t("contact.attach")}</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                  onChange={pick}
                  className="hidden"
                  data-testid="contact-attachment-input"
                />
                {!file ? (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    data-testid="contact-attachment-button"
                    className="font-mono2 flex w-full items-center justify-center gap-2 rounded border border-dashed border-white/20 px-4 py-4 text-[11px] uppercase tracking-[0.2em] text-slate-400 transition-colors hover:border-amber-500 hover:text-amber-500"
                  >
                    <Paperclip size={14} /> {t("contact.attachCta")}
                  </button>
                ) : (
                  <div className="flex items-center justify-between gap-3 rounded border border-amber-600/40 bg-[#141009] px-4 py-3" data-testid="contact-attachment-chip">
                    <span className="font-mono2 truncate text-xs text-amber-500">{file.name}</span>
                    <button type="button" onClick={clearFile} data-testid="contact-attachment-remove" className="text-slate-400 transition-colors hover:text-white">
                      <X size={16} />
                    </button>
                  </div>
                )}
                <p className="font-mono2 mt-2 text-[10px] tracking-wider text-slate-600">{t("contact.attachHint")}</p>
              </div>

              <button
                disabled={sending}
                className="font-display w-full rounded bg-amber-600 py-3.5 text-sm uppercase tracking-[0.2em] text-black transition-colors hover:bg-amber-500 disabled:opacity-50"
                data-testid="contact-form-submit"
              >
                {sending ? t("contact.sendingLabel") : t("contact.submit")}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
      <Footer />
    </div>
  );
}
